import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { forkJoin, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import type {
  EducationalKbArtifactOverlay,
  EducationalKbBundle,
  EducationalKbManifest,
  EducationalSharedSlotsConfig,
  MergedEducationalArtifactConfig,
} from './educational-workflow-config.types';

const KB_BASE = 'assets/knowledge-base';

const EMPTY_KB_BUNDLE: EducationalKbBundle = {
  manifest: { version: '0', sharedSlotsUrl: '', artifacts: [] },
  shared: { version: '0', payloadMapping: {}, slots: {} },
  artifacts: {},
};

@Injectable({ providedIn: 'root' })
export class EducationalWorkflowConfigService {
  private readonly http = inject(HttpClient);

  /** First subscription triggers HTTP loads; result is replayed. */
  private readonly bundle$ = this.http.get<EducationalKbManifest>(`${KB_BASE}/manifest.json`).pipe(
    switchMap(manifest => {
      const shared$ = this.http.get<EducationalSharedSlotsConfig>(
        `${KB_BASE}/${manifest.sharedSlotsUrl}`
      );
      const perArtifact$ = forkJoin(
        manifest.artifacts.map(entry =>
          this.http
            .get<EducationalKbArtifactOverlay>(`${KB_BASE}/${entry.configUrl}`)
            .pipe(map(cfg => ({ key: entry.key, cfg })))
        )
      ).pipe(
        map(rows => {
          const artifacts: Record<string, EducationalKbArtifactOverlay> = {};
          for (const { key, cfg } of rows) {
            artifacts[key] = cfg;
          }
          return artifacts;
        })
      );
      return forkJoin({ shared: shared$, artifacts: perArtifact$ }).pipe(
        map(({ shared, artifacts }) => ({
          manifest,
          shared,
          artifacts,
        }))
      );
    }),
    catchError(err => {
      console.error('[EducationalWorkflowConfig] failed to load knowledge base', err);
      return of(EMPTY_KB_BUNDLE);
    }),
    shareReplay(1)
  );

  /**
   * Loads manifest, shared slot config, and all artifact overlays (lazy on first subscribe).
   */
  ensureLoaded(): Observable<EducationalKbBundle> {
    return this.bundle$;
  }

  getSharedSlots(): Observable<EducationalSharedSlotsConfig> {
    return this.bundle$.pipe(map(b => b.shared));
  }

  getArtifactOverlay(artifactKey: string): Observable<EducationalKbArtifactOverlay | null> {
    return this.bundle$.pipe(map(b => b.artifacts[artifactKey] ?? null));
  }

  getMergedArtifactConfig(artifactKey: string): Observable<MergedEducationalArtifactConfig | null> {
    return this.bundle$.pipe(
      map(b => {
        const artifact = b.artifacts[artifactKey];
        if (!artifact) {
          return null;
        }
        return { artifactKey, shared: b.shared, artifact };
      })
    );
  }

  /** Alias for {@link getMergedArtifactConfig} (foundation plan naming). */
  getArtifactConfig(artifactKey: string): Observable<MergedEducationalArtifactConfig | null> {
    return this.getMergedArtifactConfig(artifactKey);
  }
}
